import type { ComponentPropsWithRef, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { forwardRef } from 'react';

import { CircleLoader } from '@proton/atoms/CircleLoader/CircleLoader';
import clsx from '@proton/utils/clsx';

import DropdownCaret from '../dropdown/DropdownCaret';
import type { NodeOrBoolean } from '../v2/field/InputField';

interface SelectButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'value'> {
    unstyled?: boolean;
    loading?: boolean;
    error?: NodeOrBoolean;
    isOpen?: boolean;
    onOpen?: () => void;
    noCaret?: boolean;
    caretIcon?: ReactElement;
    caretClassName?: string;
    fullWidth?: boolean;
    prefixIcon?: ReactNode;
}

const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(
    (
        {
            className,
            unstyled,
            loading,
            error,
            isOpen,
            onOpen,
            children,
            noCaret,
            caretIcon,
            caretClassName,
            fullWidth = true,
            prefixIcon,
            ...rest
        },
        ref
    ) => {
        const handleAnchorKeydown = (e: KeyboardEvent<HTMLButtonElement>) => {
            switch (e.key) {
                case ' ': {
                    onOpen?.();
                    break;
                }

                default:
            }
        };

        return (
            // eslint-disable-next-line jsx-a11y/role-supports-aria-props
            <button
                ref={ref}
                type="button"
                onKeyDown={handleAnchorKeydown}
                aria-expanded={isOpen}
                aria-busy={loading}
                aria-live="assertive"
                aria-atomic="true"
                aria-invalid={Boolean(error)}
                className={clsx([
                    !unstyled && 'select field',
                    unstyled && 'select-unstyled',
                    isOpen && 'select--open',
                    'outline-none flex justify-space-between items-center flex-nowrap *:pointer-events-none',
                    fullWidth && 'w-full',
                    className,
                ])}
                {...rest}
            >
                {prefixIcon && <span className="inline-flex">{prefixIcon}</span>}
                <span className="flex-1 text-ellipsis text-left">{children}</span>

                {
                    // eslint-disable-next-line no-nested-ternary
                    loading ? (
                        <CircleLoader className="shrink-0 ml-1" />
                    ) : noCaret ? null : (
                        <DropdownCaret
                            className={clsx('shrink-0 ml-1', caretClassName)}
                            icon={caretIcon}
                            isOpen={isOpen}
                        />
                    )
                }
            </button>
        );
    }
);

SelectButton.displayName = 'SelectButton';
export default SelectButton;
